import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Box, HStack, Spinner, Text, VStack } from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { RiftboundPlayerAvatar } from '../../../components/RiftboundPlayerAvatar';
import { SlugText } from '../../../components/SlugText';
import { RiftboundInput } from '../../../components/ui/input';

export interface PlayerDirectoryPick {
  uid: string;
  displayName: string;
  slug: string;
  avatarUrl: string | null;
}

export interface PlayerDirectoryTypeaheadProps {
  onSelect: (player: PlayerDirectoryPick) => void;
  excludeUids?: string[];
  placeholder?: string;
  disabled?: boolean;
  maxResults?: number;
}

const SEARCH_DEBOUNCE_MS = 200;
/**
 * Fetch more than we render so the caller's `excludeUids` (team members, who
 * are the most likely matches) cannot empty the visible list. Excluding
 * server-side instead would make the response vary per team and defeat the
 * shared CDN cache.
 */
const FETCH_LIMIT = 25;

/**
 * Bounds how stale the typeahead can be. This used to be an `onSnapshot`, so a
 * player who registered while the invite panel was open appeared immediately;
 * an unbounded cache would hide them until reload.
 */
const CLIENT_CACHE_TTL_MS = 60_000;

interface SearchResult {
  players: PlayerDirectoryPick[];
  /** The server had more matches than it returned, so this set is a partial answer. */
  truncated: boolean;
  fetchedAt: number;
}

/**
 * Query text → results. Typing forward and then backspacing is the common
 * interaction, and re-requesting a query the user already typed is pure waste.
 */
const searchCache = new Map<string, SearchResult>();

function readCache(key: string): SearchResult | null {
  const hit = searchCache.get(key);
  if (!hit) return null;
  if (Date.now() - hit.fetchedAt > CLIENT_CACHE_TTL_MS) {
    searchCache.delete(key);
    return null;
  }
  return hit;
}

/** Matches the server's filter in api/_shared/playerDirectorySearch.js. */
function matchesQuery(player: PlayerDirectoryPick, query: string): boolean {
  if (!query) return true;
  return (
    player.displayName.toLowerCase().includes(query) ||
    player.slug.toLowerCase().includes(query)
  );
}

/**
 * Narrow an already-complete result set instead of asking the server again.
 *
 * Substring matching is monotonic under extension: if `key` starts with the
 * cached query, every player containing `key` also contains that query, so the
 * matches for `key` are exactly this subset. Only safe when the cached set was
 * not truncated — otherwise the missing rows are unknowable locally.
 */
function narrowFromCache(key: string): SearchResult | null {
  for (const cachedKey of [...searchCache.keys()]) {
    if (!key.startsWith(cachedKey)) continue;
    const cached = readCache(cachedKey);
    if (!cached || cached.truncated) continue;
    return {
      players: cached.players.filter((player) => matchesQuery(player, key)),
      truncated: false,
      fetchedAt: cached.fetchedAt,
    };
  }
  return null;
}

function parsePlayers(payload: unknown): SearchResult {
  const source = payload as { players?: unknown; truncated?: unknown } | null;
  const rows = source?.players;
  const players: PlayerDirectoryPick[] = [];
  if (Array.isArray(rows)) {
    for (const row of rows) {
      if (!row || typeof row !== 'object') continue;
      const { uid, displayName, slug, avatarUrl } = row as Record<string, unknown>;
      if (typeof uid !== 'string' || !uid) continue;
      if (typeof slug !== 'string' || !slug) continue;
      players.push({
        uid,
        displayName: typeof displayName === 'string' && displayName ? displayName : slug,
        slug,
        avatarUrl: typeof avatarUrl === 'string' && avatarUrl ? avatarUrl : null,
      });
    }
  }
  return { players, truncated: source?.truncated === true, fetchedAt: Date.now() };
}

async function searchPlayers(key: string, signal: AbortSignal): Promise<SearchResult> {
  // `/api/players` is a vercel.json rewrite onto the sitemap function, which
  // supplies `action=player-search` — do not send an `action` param here.
  const params = new URLSearchParams({ q: key, limit: String(FETCH_LIMIT) });
  const res = await fetch(`/api/players?${params.toString()}`, { signal });
  if (!res.ok) throw new Error(`Player search failed (${res.status})`);
  const result = parsePlayers(await res.json());
  searchCache.set(key, result);
  return result;
}

export function PlayerDirectoryTypeahead({
  onSelect,
  excludeUids = [],
  placeholder = 'Search players by name or handle…',
  disabled = false,
  maxResults = 6,
}: PlayerDirectoryTypeaheadProps) {
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<PlayerDirectoryPick[]>(() => readCache('')?.players ?? []);
  const [loading, setLoading] = useState(() => !readCache(''));
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const exclude = useMemo(() => new Set(excludeUids), [excludeUids]);

  // Matching happens server-side now, so resolve from the cache first (exact
  // hit, or a narrowed complete superset) and only fall back to a debounced,
  // abortable request.
  useEffect(() => {
    const key = query.trim().toLowerCase();
    const resolved = readCache(key) ?? narrowFromCache(key);
    if (resolved) {
      searchCache.set(key, resolved);
      setRows(resolved.players);
      setLoading(false);
      return;
    }

    setLoading(true);
    const controller = new AbortController();
    const timer = setTimeout(() => {
      searchPlayers(key, controller.signal)
        .then((result) => {
          setRows(result.players);
          setLoading(false);
        })
        .catch((err: unknown) => {
          if (controller.signal.aborted) return;
          console.warn('[PlayerDirectoryTypeahead] search failed', err);
          setRows([]);
          setLoading(false);
        });
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [query]);

  const results = useMemo(
    () => rows.filter((r) => !exclude.has(r.uid)).slice(0, maxResults),
    [rows, exclude, maxResults],
  );

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const showDropdown = open && (loading || results.length > 0 || query.trim().length > 0);

  return (
    <Box ref={rootRef} position="relative" w="full">
      <HStack
        borderWidth="hairline"
        borderColor="border"
        borderRadius="md"
        bg="bgElevated"
        px={3}
        gap={2}
      >
        <Box color="textMuted" fontSize="md" flexShrink={0}>
          <MdSearch />
        </Box>
        <RiftboundInput
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          border="none"
          bg="transparent"
          color="text"
          _focusVisible={{ outline: 'none', boxShadow: 'none' }}
          px={0}
          aria-autocomplete="list"
          aria-controls={listId}
          aria-expanded={showDropdown}
        />
        {loading ? <Spinner size="xs" color="accent" /> : null}
      </HStack>

      {showDropdown ? (
        <VStack
          id={listId}
          role="listbox"
          align="stretch"
          gap={1}
          position="absolute"
          top="100%"
          left={0}
          right={0}
          mt={1}
          p={1}
          bg="bgElevated"
          borderWidth="hairline"
          borderColor="border"
          borderRadius="md"
          zIndex={20}
          maxH="280px"
          overflowY="auto"
          boxShadow="md"
        >
          {!loading && results.length === 0 ? (
            <Text fontSize="sm" color="textMuted" px={3} py={2}>
              No players found
            </Text>
          ) : null}
          {results.map((player) => (
            <Box
              key={`${player.uid}:${player.slug}`}
              role="option"
              as="button"
              w="full"
              textAlign="left"
              display="flex"
              alignItems="center"
              gap={3}
              px={2}
              py={2}
              borderRadius="md"
              borderWidth="hairline"
              borderColor="transparent"
              cursor="pointer"
              color="text"
              _hover={{ bg: 'bg.emphasized', borderColor: 'accent' }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                onSelect(player);
                setQuery('');
                setOpen(false);
              }}
            >
              <RiftboundPlayerAvatar
                name={player.displayName}
                photoURL={player.avatarUrl}
                size="sm"
                flexShrink={0}
              />
              <Box minW={0} flex={1}>
                <Text fontSize="sm" fontWeight="semibold" lineClamp={1}>
                  {player.displayName}
                </Text>
                <Text fontSize="xs" color="textMuted">
                  <SlugText slug={player.slug} />
                </Text>
              </Box>
            </Box>
          ))}
        </VStack>
      ) : null}
    </Box>
  );
}
